import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./VenueUpdatePage.module.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Autocomplete,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Checkbox,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloseIcon from "@mui/icons-material/Close";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GET_LEAGUES,
  GET_LEAGUE_TEAMS_BY_LEAGUE_ID,
  GET_VENUES_BY_LEAGUE_AND_TEAM,
  ADD_VENUE_TO_TEAM,
  ADDING_MULTIPLE_VENUES_INTO_A_TEAM,
  UPDATE_LEAGUE_TEAM_VENUE_AVID_BY_ID,
  DELETE_VENUE_NAME_BY_ID,
  GET_ALL_VENUES_WITH_NAME_FILTER,
} from "./api/VenueUpdatePageQuery";
import { toast } from "react-toastify";
import ConfirmDeleteDialog from "../VenueUpdate/model/ConfirmDeleteDialog";

interface Venue {
  ltv_id: string;
  ltv_name: string;
  av_id?: string;
  av_name?: string;
  av_address?: string;
  av_city?: string;
  av_state_code?: string;
  av_street?: string;  // Add this
  av_zipcode?: string; // Add this if needed
  av_country_code?: string; // Add this if needed
  all_venue?: {
    av_id: string;
    av_name: string;
    av_venue_ref_id?: string;
    av_timezone?: string;
    av_latitude?: number;
    av_longitude?: number;
    av_street?: string;
    av_city?: string;
    av_state_code?: string;
    av_zipcode?: string;
    av_country_code?: string;
  } | null;
}

interface League {
  l_id: string;
  l_name: string;
  l_type: string;
}

interface Team {
  lt_id: string;
  lt_name: string;
}

const VenueUpdatePage: React.FC = () => {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [filteredVenues, setFilteredVenues] = useState<Venue[]>([]);
  const [availableVenues, setAvailableVenues] = useState<Venue[]>([]);
  
  const [leagueId, setLeagueId] = useState<string>("");
  const [teamId, setTeamId] = useState<string>("");
  
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  useEffect(() => {
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
    if (darkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
      document.body.classList.add('dark-mode');
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
      document.body.classList.remove('dark-mode');
    }
  }, [darkMode]);
  
  const [searchVenueName, setSearchVenueName] = useState("");
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deleteVenueTarget, setDeleteVenueTarget] = useState<{ id: string; name: string }>({
    id: "",
    name: "",
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  
  const [inputValue, setInputValue] = useState("");
  const [selectedVenues, setSelectedVenues] = useState<Venue[]>([]);
  
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const tableSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const latestVenueSearchRef = useRef<string>("");
  
  const [getLeagues] = useLazyQuery(GET_LEAGUES, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      console.log("GET_LEAGUES Completed:", data);
      setLeagues(data?.leagues || []);
      if (data?.leagues?.length > 0) {
        setLeagueId(data.leagues[0].l_id);
      }
      setLoading(false);
    },
    onError: (err) => {
      console.error("Error fetching leagues:", err);
      toast.error("Failed to load leagues");
      setLoading(false);
    },
  });

  const [getTeams] = useLazyQuery(GET_LEAGUE_TEAMS_BY_LEAGUE_ID, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      console.log("GET_LEAGUE_TEAMS Completed:", data);
      setTeams(data?.league_teams || []);
      setCurrentPage(1);
      if (data?.league_teams?.length > 0) {
        setTeamId(data.league_teams[0].lt_id);
      }
    },
    onError: (err) => {
      console.error("Error fetching teams:", err);
      toast.error("Failed to load teams");
    },
  });

  const [getVenues] = useLazyQuery(GET_VENUES_BY_LEAGUE_AND_TEAM, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      console.log("✅ GET_VENUES Completed:", data);
      console.log("📱 Data sent to mobile:", JSON.stringify(data, null, 2));
      
      const venuesData = data?.league_team_venues || [];
      
      // Log venue details for debugging
      venuesData.forEach((venue: Venue, idx: number) => {
        console.log(`Venue ${idx + 1}:`, {
          name: venue.all_venue?.av_name || venue.ltv_name,
          hasLat: !!venue.all_venue?.av_latitude,
          hasLng: !!venue.all_venue?.av_longitude,
          hasTimezone: !!venue.all_venue?.av_timezone,
          fullAddress: venue.all_venue?.av_street,
          city: venue.all_venue?.av_city,
          state: venue.all_venue?.av_state_code
        });
      });
      
      setVenues(venuesData);
      setFilteredVenues(venuesData);
      setCurrentPage(1);
      setTableLoading(false);
    },
    onError: (err) => {
      console.error("Error fetching venues:", err);
      toast.error("Failed to load venues");
      setTableLoading(false);
    },
  });

  const [getAllAvailableVenues] = useLazyQuery(GET_ALL_VENUES_WITH_NAME_FILTER, {
    fetchPolicy: "no-cache",
    onError: (err) => {
      console.error("❌ Error fetching available venues:", err);
      toast.error("Failed to load available venues");
    },
  });

  const [addVenue] = useMutation(ADD_VENUE_TO_TEAM, {
    onCompleted: () => {
      setErrorMessage("");
      handleCloseModal();
      if (leagueId && teamId) fetchVenues();
      toast.success("Venue added successfully");
    },
    onError: (err) => {
      console.error("Error adding venue:", err);
      if (err.message.includes("unique constraint")) {
        setErrorMessage("This venue is already assigned to this team.");
      } else {
        setErrorMessage("Error adding venue. Please try again.");
      }
    },
  });

  const [addMultipleVenues] = useMutation(ADDING_MULTIPLE_VENUES_INTO_A_TEAM, {
    onCompleted: () => {
      setErrorMessage("");
      handleCloseModal();
      if (leagueId && teamId) fetchVenues();
      toast.success("Venues added successfully");
    },
    onError: (err) => {
      console.error("Error adding venues:", err);
      setErrorMessage("Error adding one or more venues. They may already exist.");
    },
  });

  const [updateVenue] = useMutation(UPDATE_LEAGUE_TEAM_VENUE_AVID_BY_ID, {
    onCompleted: () => {
      setErrorMessage("");
      handleCloseModal();
      if (leagueId && teamId) fetchVenues();
      toast.success("Venue updated successfully");
    },
    onError: (err) => {
      console.error("Error updating venue:", err);
      if (err.message.includes("unique constraint")) {
        setErrorMessage("This venue is already assigned to this team.");
      } else {
        setErrorMessage("Error updating venue. Please try again.");
      }
    },
  });

  const [deleteVenue] = useMutation(DELETE_VENUE_NAME_BY_ID, {
    onCompleted: () => {
      toast.success("Venue deleted successfully");
      if (leagueId && teamId) fetchVenues();
    },
    onError: (err) => {
      console.error("Error deleting venue:", err);
      toast.error("Failed to delete venue");
    },
  });

  const fetchVenues = useCallback(() => {
    if (leagueId && teamId) {
      console.log(`Fetching venues for league: ${leagueId}, team: ${teamId}`);
      setTableLoading(true);
      getVenues({
        variables: { l_id: leagueId, lt_id: teamId },
      });
    }
  }, [leagueId, teamId, getVenues]);

  const handleVenueSearch = useCallback((searchText: string) => {
    setInputValue(searchText);
    const normalizedSearch = searchText.trim().toLowerCase();
    latestVenueSearchRef.current = normalizedSearch;
    
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (normalizedSearch.length < 2) {
      setAvailableVenues([]);
      return;
    }
    
    searchTimeoutRef.current = setTimeout(async () => {
      const searchSnapshot = normalizedSearch;
      const response = await getAllAvailableVenues({
        variables: { name: `%${searchText.trim()}%`, limit: 50 },
      });

      if (latestVenueSearchRef.current !== searchSnapshot) return;

      const rawVenues = response?.data?.all_venues || [];
      const uniqueVenuesMap = new Map<string, Venue>();
      rawVenues.forEach((venue: Venue) => {
        if (venue?.av_id && !uniqueVenuesMap.has(venue.av_id)) {
          uniqueVenuesMap.set(venue.av_id, venue);
        }
      });
      const uniqueVenues = Array.from(uniqueVenuesMap.values());

      const filteredBySearch = uniqueVenues.filter((venue) =>
        (venue.av_name || "").toLowerCase().includes(searchSnapshot)
      );

      const getVenueRank = (venue: Venue) => {
        const name = (venue.av_name || "").toLowerCase();
        if (name === searchSnapshot) return 0;
        if (name.startsWith(searchSnapshot)) return 1;
        return 2;
      };

      const sortedVenues = [...filteredBySearch].sort((a, b) => {
        const rankDiff = getVenueRank(a) - getVenueRank(b);
        if (rankDiff !== 0) return rankDiff;
        return (a.av_name || "").localeCompare(b.av_name || "");
      });

      setAvailableVenues(sortedVenues);
    }, 250);
  }, [getAllAvailableVenues]);

  const handleTableSearch = useCallback((searchText: string) => {
    if (tableSearchTimeoutRef.current) clearTimeout(tableSearchTimeoutRef.current);
    
    tableSearchTimeoutRef.current = setTimeout(() => {
      if (searchText.trim() === "") {
        setFilteredVenues(venues);
      } else {
        const filtered = venues.filter((venue) => {
          const venueName = venue.all_venue?.av_name || venue.ltv_name || "";
          const venueId = venue.av_id || venue.all_venue?.av_id || venue.ltv_id || "";
          return (
            venueName.toLowerCase().includes(searchText.toLowerCase()) ||
            venueId.toLowerCase().includes(searchText.toLowerCase())
          );
        });
        setFilteredVenues(filtered);
      }
      setCurrentPage(1);
    }, 300);
  }, [venues]);

  const handleTableSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchVenueName(value);
    handleTableSearch(value);
  };

  const handleClearTableSearch = () => {
    setSearchVenueName("");
    setFilteredVenues(venues);
    setCurrentPage(1);
    if (tableSearchTimeoutRef.current) clearTimeout(tableSearchTimeoutRef.current);
  };

  useEffect(() => {
    console.log("Component mounted - fetching leagues");
    setLoading(true);
    getLeagues();
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (tableSearchTimeoutRef.current) clearTimeout(tableSearchTimeoutRef.current);
    };
  }, [getLeagues]);

  useEffect(() => {
    if (leagueId) {
      console.log(`League changed to: ${leagueId} - fetching teams`);
      getTeams({ variables: { l_id: leagueId } });
      setTeamId("");
      setVenues([]);
      setFilteredVenues([]);
      setSearchVenueName("");
    }
  }, [leagueId, getTeams]);

  useEffect(() => {
    if (leagueId && teamId) {
      console.log(`Team changed to: ${teamId} - fetching venues`);
      fetchVenues();
    }
  }, [teamId, fetchVenues]);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handlePageChange = (direction: "prev" | "next") => {
    const totalPages = Math.ceil(filteredVenues.length / rowsPerPage);
    if (direction === "prev" && currentPage > 1) setCurrentPage(currentPage - 1);
    else if (direction === "next" && currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handleDelete = (id: string, name: string) => {
    setDeleteVenueTarget({ id, name });
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteVenueTarget.id) return;
    try {
      await deleteVenue({ variables: { ltv_id: deleteVenueTarget.id } });
    } catch (err) {
      console.error(err);
    } finally {
      setOpenDeleteModal(false);
      setDeleteVenueTarget({ id: "", name: "" });
    }
  };

  const handleOpenAdd = () => {
    console.log("📂 Opening add venue modal");
    setIsEditMode(false);
    setSelectedVenues([]);
    setAvailableVenues([]);
    setInputValue("");
    setOpenAddModal(true);
    setErrorMessage("");
  };

  const handleEdit = (venue: Venue) => {
    console.log("Editing venue:", venue);
    setIsEditMode(true);
    setEditingVenue(venue);
    const selectedVenue = {
      av_id: venue.all_venue?.av_id || venue.av_id,
      av_name: venue.all_venue?.av_name || venue.ltv_name,
      av_address: venue.all_venue?.av_street || venue.av_address,
      av_city: venue.all_venue?.av_city || venue.av_city,
      av_state_code: venue.all_venue?.av_state_code || venue.av_state_code,
    } as Venue;
    setSelectedVenues([selectedVenue]);
    setAvailableVenues([]);
    setInputValue(selectedVenue.av_name || "");
    setOpenAddModal(true);
    setErrorMessage("");
  };

  const handleSave = async () => {
    if (selectedVenues.length === 0 || !leagueId || !teamId) return;

    if (isEditMode && editingVenue) {
      const venue = selectedVenues[0];
      const selectedAvId = venue?.av_id || venue?.ltv_id;
      await updateVenue({
        variables: { ltv_id: editingVenue.ltv_id, av_id: selectedAvId },
      });
    } else {
      const venuesToInsert = selectedVenues.map((venue) => ({
        lt_id: teamId,
        av_id: venue.av_id,
        ltv_name: venue.av_name || venue.ltv_name || "Unknown",
      }));
      
      try {
        await addMultipleVenues({ variables: { venues: venuesToInsert } });
        handleCloseModal();
        fetchVenues();
      } catch (err) {
        console.error(err);
        let addedCount = 0;
        for (const venue of selectedVenues) {
          const selectedAvId = venue?.av_id;
          const venueName = venue.av_name || venue.ltv_name || "Unknown";
          const isDuplicate = venues.some((v) => (v.all_venue?.av_id || v.av_id) === selectedAvId);
          if (isDuplicate) {
            toast.warning(`${venueName} already exists`);
            continue;
          }
          try {
            await addVenue({
              variables: { lt_id: teamId, av_id: selectedAvId, ltv_name: venueName },
            });
            addedCount++;
          } catch (err) { /* ignore */ }
        }
        if (addedCount > 0) {
          toast.success(`${addedCount} venue(s) added`);
          handleCloseModal();
          fetchVenues();
        }
      }
    }
  };

  const handleCloseModal = () => {
    setInputValue("");
    setSelectedVenues([]);
    setEditingVenue(null);
    setIsEditMode(false);
    setOpenAddModal(false);
    setErrorMessage("");
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  };

  const totalPages = Math.ceil(filteredVenues.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedVenues = filteredVenues.slice(startIndex, startIndex + rowsPerPage);

  return (
    <div className={`${styles.container} ${darkMode ? styles.darkMode : ""}`}>
      <div className={styles.header}>
        <div>
          <p className={styles.subtitle}>Manage Venue Assignments</p>
          <h1 className={styles.title}>Venue Update</h1>
        </div>
        <div className={styles.searchSection}>
          <div className={styles.searchWrapper}>
            <SearchIcon className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Search by Venue Name/ID..."
              className={styles.searchInput}
              value={searchVenueName}
              onChange={handleTableSearchChange}
            />
            {searchVenueName && (
              <button onClick={handleClearTableSearch} className={styles.clearSearchBtn}>✕</button>
            )}
          </div>
          <div className={styles.iconGroup}>
            <button
              className={styles.iconBtn}
              onClick={() => {
                if (leagueId && teamId) fetchVenues();
                handleClearTableSearch();
              }}
              disabled={tableLoading}
            >
              <RefreshRoundedIcon style={{ opacity: tableLoading ? 0.5 : 1 }} />
            </button>
            <button className={styles.iconBtn} onClick={() => setDarkMode(!darkMode)}>
              {darkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <div className={styles.filterRow}>
          <div className={styles.formGroup}>
            <label>League *</label>
            <select value={leagueId} onChange={(e) => setLeagueId(e.target.value)} disabled={loading}>
              <option value="">Select a league</option>
              {leagues.map((league) => (
                <option key={league.l_id} value={league.l_id}>{league.l_name}</option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label>Home Team *</label>
            <select
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              disabled={!leagueId || teams.length === 0 || loading}
            >
              <option value="">Select a team</option>
              {teams.map((team) => (
                <option key={team.lt_id} value={team.lt_id}>{team.lt_name}</option>
              ))}
            </select>
          </div>
          <button className={styles.addVenueBtn} onClick={handleOpenAdd}>+ Add Venue</button>
        </div>
      </div>

      {searchVenueName && filteredVenues.length > 0 && (
        <div className={styles.searchSummary}>
          <p>Found <strong>{filteredVenues.length}</strong> venue(s) matching "{searchVenueName}"</p>
        </div>
      )}

      <div className={styles.tableCard}>
        {tableLoading ? (
          <div className={styles.loadingContainer}><CircularProgress /></div>
        ) : filteredVenues.length === 0 ? (
          <div className={styles.emptyState}>
            <LocationOnIcon className={styles.emptyStateIcon} />
            <p className={styles.emptyStateTitle}>
              {searchVenueName ? "No matching venues found" : "No venues assigned yet"}
            </p>
            <p className={styles.emptyStateSubtitle}>
              {searchVenueName ? `No venues match "${searchVenueName}"` : 'Click "Add Venue" to get started.'}
            </p>
            {searchVenueName && (
              <button onClick={handleClearTableSearch} className={styles.clearSearchBtnPrimary}>Clear Search</button>
            )}
          </div>
        ) : (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>VENUE</th>
                    <th>VENUE ID</th>
                    <th>LOCATION</th>
                    <th>ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedVenues.map((venue) => {
                    const fullAddress = [
                      venue.all_venue?.av_street,
                      venue.all_venue?.av_city,
                      venue.all_venue?.av_state_code,
                      venue.all_venue?.av_zipcode
                    ].filter(Boolean).join(', ');
                    
                    const locationText = fullAddress || 
                      [venue.all_venue?.av_city, venue.all_venue?.av_state_code].filter(Boolean).join(', ');
                    
                    const timezoneInfo = venue.all_venue?.av_timezone ? `\nTimezone: ${venue.all_venue.av_timezone}` : '';
                    const coordinatesInfo = (venue.all_venue?.av_latitude && venue.all_venue?.av_longitude) 
                      ? `\nCoordinates: ${venue.all_venue.av_latitude}, ${venue.all_venue.av_longitude}` 
                      : '';
                    
                    return (
                      <tr key={venue.ltv_id}>
                        <td>{venue.all_venue?.av_name || venue.ltv_name}</td>
                        <td>{venue.av_id || venue.all_venue?.av_id}</td>
                        <td>
                          <Tooltip 
                            title={`${locationText || "No location"}${timezoneInfo}${coordinatesInfo}`} 
                            arrow
                          >
                            <span style={{ display: 'block', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {locationText || "—"}
                            </span>
                          </Tooltip>
                        </td>
                        <td className={styles.actionCol}>
                          <button className={styles.editBtn} onClick={() => handleEdit(venue)}>✎ Edit</button>
                          <button className={styles.deleteBtn} onClick={() => handleDelete(venue.ltv_id, venue.ltv_name)}>🗑 Delete</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className={styles.tableFooter}>
              <div className={styles.paginationControls}>
                <div className={styles.rowsPerPage}>
                  <label>Rows per page:</label>
                  <select value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <div className={styles.paginationInfo}>
                  <span>Showing {startIndex + 1} - {Math.min(startIndex + rowsPerPage, filteredVenues.length)} of {filteredVenues.length}</span>
                  <div className={styles.paginationButtons}>
                    <button onClick={() => handlePageChange("prev")} disabled={currentPage === 1}>← Previous</button>
                    <span className={styles.pageNumber}>Page {currentPage} of {totalPages || 1}</span>
                    <button onClick={() => handlePageChange("next")} disabled={currentPage >= totalPages}>Next →</button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <Dialog 
        open={openAddModal} 
        onClose={handleCloseModal} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ 
          sx: { 
            borderRadius: "20px", 
            padding: "24px", 
            backgroundColor: darkMode ? "#1e293b" : "#ffffff", 
            maxWidth: "600px", 
            margin: "16px" 
          } 
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: 600, fontSize: "20px", padding: "0 0 20px 0", color: darkMode ? "#f1f5f9" : "#111827" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "12px", backgroundColor: darkMode ? "#334155" : "#EEF2FF", display: "flex", alignItems: "center", justifyContent: "center", color: "#5D75F8" }}>
              {isEditMode ? <EditIcon sx={{ fontSize: 22 }} /> : <LocationOnIcon sx={{ fontSize: 22 }} />}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "18px", color: darkMode ? "#f1f5f9" : "#111827" }}>
              {isEditMode ? "Edit Venue" : "Add Venues"}
            </Typography>
          </Box>
          <CloseIcon onClick={handleCloseModal} sx={{ cursor: "pointer", fontSize: 20, color: darkMode ? "#94a3b8" : "#6B7280", "&:hover": { color: darkMode ? "#f1f5f9" : "#111827" } }} />
        </DialogTitle>

        <DialogContent sx={{ padding: "0 0 20px 0" }}>
          {errorMessage && (
            <Box sx={{ backgroundColor: darkMode ? "#7f1d1d" : "#FEE2E2", border: `1px solid ${darkMode ? "#991b1b" : "#FCA5A5"}`, borderRadius: "10px", padding: "12px 16px", marginBottom: "20px" }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 500, color: darkMode ? "#fca5a5" : "#DC2626" }}>{errorMessage}</Typography>
            </Box>
          )}
          {isEditMode && (
            <Typography sx={{ fontSize: "14px", fontWeight: 500, color: darkMode ? "#cbd5e1" : "#374151", marginBottom: "12px" }}>
              Update venue assignment
            </Typography>
          )}
          <Typography sx={{ fontSize: "13px", fontWeight: 400, color: darkMode ? "#94a3b8" : "#6b7280", marginBottom: "20px" }}>
            {isEditMode ? "Select a different venue to update this assignment" : "Type at least 2 characters to search venues. You can select multiple venues."}
          </Typography>

          <Box sx={{ width: "100%" }}>
            <Autocomplete
              multiple={!isEditMode}
              fullWidth
              openOnFocus
              disableCloseOnSelect={!isEditMode}
              clearOnBlur={false}
              options={availableVenues}
              filterOptions={(options) => options}
              value={selectedVenues}
              inputValue={inputValue}
              onInputChange={(_, newInputValue, reason) => {
                setInputValue(newInputValue);
                if (reason === "input") {
                  if (newInputValue.length >= 2) {
                    handleVenueSearch(newInputValue);
                  } else {
                    setAvailableVenues([]);
                  }
                }
              }}
              onChange={(_, value) => {
                if (isEditMode) setSelectedVenues(value ? [value as Venue] : []);
                else setSelectedVenues((value as Venue[]) || []);
              }}
              getOptionLabel={(option) => option.av_name || option.ltv_name || ""}
              isOptionEqualToValue={(option, value) => (option.av_id || option.ltv_id) === (value?.av_id || value?.ltv_id)}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: "8px 16px" }}>
                  {!isEditMode && <Checkbox checked={selected} style={{ marginRight: 12, padding: 0 }} />}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500, marginBottom: "4px", color: darkMode ? "#f1f5f9" : "#111827" }}>
                      {option.av_name || option.ltv_name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: darkMode ? "#94a3b8" : "#6b7280" }}>
                      ID: {option.av_id || option.ltv_id}
                    </Typography>
                    {(option.av_address || option.av_city || option.av_state_code) && (
                      <Typography variant="caption" sx={{ display: 'block', color: darkMode ? "#94a3b8" : "#6b7280", mt: 0.5 }}>
                        {[option.av_street || option.av_address, option.av_city, option.av_state_code].filter(Boolean).join(', ')}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderTags={(value, getTagProps) => value.map((option, index) => (
                <Chip
                  label={option.av_name || option.ltv_name}
                  {...getTagProps({ index })}
                  key={option.av_id || option.ltv_id}
                  size="small"
                  sx={{ backgroundColor: darkMode ? "#334155" : "#EEF2FF", color: "#5D75F8", fontWeight: 500 }}
                />
              ))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Venues"
                  placeholder={isEditMode ? "Type at least 2 chars..." : "Type at least 2 chars to search..."}
                  variant="outlined"
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": { 
                      borderRadius: "12px", 
                      backgroundColor: darkMode ? "#334155" : "#F9FAFB", 
                      "& .MuiOutlinedInput-input": { color: darkMode ? "#f1f5f9" : "#111827" } 
                    },
                    "& .MuiInputLabel-root": { fontSize: "14px", color: darkMode ? "#94a3b8" : "#6b7280" },
                  }}
                />
              )}
              ListboxProps={{ sx: { maxHeight: 320 } }}
            />
          </Box>

          {selectedVenues.length > 0 && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: darkMode ? "#334155" : "#F9FAFB", borderRadius: "12px", border: `1px solid ${darkMode ? "#475569" : "#E5E7EB"}` }}>
              <Typography variant="caption" sx={{ mb: 1.5, display: 'block', fontWeight: 600, fontSize: "12px", color: darkMode ? "#cbd5e1" : "#374151", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Selected Venues ({selectedVenues.length}):
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {selectedVenues.map((venue, index) => (
                  <Box key={index} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", backgroundColor: darkMode ? "#1e293b" : "#FFFFFF", borderRadius: "8px", border: `1px solid ${darkMode ? "#475569" : "#E5E7EB"}` }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: darkMode ? "#f1f5f9" : "#111827" }}>
                        {venue.av_name || venue.ltv_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: darkMode ? "#94a3b8" : "#6b7280" }}>
                        ID: {venue.av_id || venue.ltv_id}
                      </Typography>
                      {(venue.av_street || venue.av_address || venue.av_city || venue.av_state_code) && (
                        <Typography variant="caption" sx={{ display: 'block', color: darkMode ? "#94a3b8" : "#6b7280", mt: 0.5 }}>
                          {[venue.av_street || venue.av_address, venue.av_city, venue.av_state_code].filter(Boolean).join(', ')}
                        </Typography>
                      )}
                    </Box>
                    {!isEditMode && (
                      <button
                        onClick={() => setSelectedVenues(prev => prev.filter((_, i) => i !== index))}
                        style={{ background: "none", border: "none", cursor: "pointer", color: darkMode ? "#94a3b8" : "#9ca3af", padding: "4px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "4px", transition: "all 0.2s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = darkMode ? "#7f1d1d" : "#FEE2E2"; e.currentTarget.style.color = "#DC2626"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = darkMode ? "#94a3b8" : "#9ca3af"; }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </button>
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: "flex-end", gap: 2, padding: "20px 0 0 0" }}>
          <Button 
            onClick={handleCloseModal} 
            sx={{ 
              textTransform: "none", 
              borderRadius: "10px", 
              px: 4, 
              py: 1, 
              backgroundColor: darkMode ? "#334155" : "#F3F4F6", 
              color: darkMode ? "#f1f5f9" : "#374151", 
              fontWeight: 600, 
              fontSize: "14px", 
              "&:hover": { backgroundColor: darkMode ? "#475569" : "#E5E7EB" } 
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={selectedVenues.length === 0} 
            sx={{ 
              textTransform: "none", 
              borderRadius: "10px", 
              px: 4, 
              py: 1, 
              fontWeight: 600, 
              fontSize: "14px", 
              backgroundColor: "#5D75F8", 
              boxShadow: "none", 
              "&:hover": { backgroundColor: "#4c63e0", boxShadow: "none" }, 
              "&.Mui-disabled": { backgroundColor: "#C7D2FE", color: "#ffffff" } 
            }}
          >
            {isEditMode ? "UPDATE VENUE" : `ADD ${selectedVenues.length} VENUE${selectedVenues.length > 1 ? 'S' : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDeleteDialog
        open={openDeleteModal}
        onClose={() => { setOpenDeleteModal(false); setDeleteVenueTarget({ id: "", name: "" }); }}
        onConfirm={handleConfirmDelete}
        venueName={deleteVenueTarget.name}
      />
    </div>
  );
};

export default VenueUpdatePage;